import React from "react";
import {
  FlucsDownIcon as ArrowDown,
  FlucsUpIcon as ArrowUp,
  UpOrDown,
} from "@picklebet/ui";

import type {
  EventStatus,
  ToggleSelectionPayload,
} from "@/hooks/firestore/betting/types";
import type { ProcessedRacingCompetitor } from "@/hooks/firestore/betting/useRacingEvents";
import type {
  ProcessedBettingCompetitor,
  ProcessedOutcome,
} from "@/hooks/firestore/betting/useEvent";

import cx from "classnames";
import { cva } from "class-variance-authority";
import { titleCase } from "title-case";
import { Odds } from "../Odds";
import { SportsIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { useIsLiveViewOnly } from "@/features/betting/hooks/useIsLiveViewOnly";
import { getDisplayOdds } from "utilities/sharedBettingUtilities";

import * as styles from "./Outcome.module.scss";

const outcomeVariants = cva(styles.container, {
  variants: {
    variant: {
      grid: styles.grid,
      card: styles.card,
      row: styles.row,
    },
  },
  defaultVariants: {
    variant: "grid",
  },
});

type OutcomeProps = {
  id: string;
  marketId?: string;
  eventId: string;
  eventStatus?: EventStatus;
  outcome: ProcessedOutcome;
  competitor?: ProcessedBettingCompetitor | ProcessedRacingCompetitor | null;
  toggleSelection: (pick: ToggleSelectionPayload) => void;
  isDraw?: boolean;
  isOutright?: boolean;
  className?: string;
  align?: string;
  isSelected?: boolean;
  oddsFormat?: string;
  isSuspended?: boolean;
  fullWidth?: boolean;
  status?: string;
  isHome?: boolean;
  isAway?: boolean;
  edges?: {
    roundBottomLeft?: boolean;
    roundBottomRight?: boolean;
    dividerLeft?: boolean;
    dividerRight?: boolean;
    bottomBorder?: boolean;
  };
  hasDraw?: boolean;
  isLoading?: boolean;
  hideCompetitor?: boolean;
  variant?: "grid" | "card" | "row";
  renderFieldsKey?: string;
  noTopBorder?: boolean;
};

const getOutcomeName = (
  outcome: ProcessedOutcome,
  competitor?: ProcessedBettingCompetitor | ProcessedRacingCompetitor | null,
  // attribute a render style groups by - its shortAbbreviation is the label to use
  renderFieldsKey?: string,
) => {
  const renderFieldAbbreviation = renderFieldsKey
    ? outcome.renderFields?.[renderFieldsKey]?.shortAbbreviation
    : undefined;

  if (renderFieldAbbreviation) {
    return renderFieldAbbreviation;
  } else if (outcome.abbreviation) {
    return outcome.abbreviation;
  } else if (outcome.name) {
    return outcome.name;
  } else if (competitor) {
    return competitor.name;
  } else {
    // just return something
    return titleCase(outcome.type ?? "");
  }
};

const Outcome = ({
  id,
  marketId: marketIdProp,
  eventId,
  eventStatus,
  toggleSelection,
  outcome,
  competitor,
  isDraw,
  isOutright,
  className: classNameProp,
  align,
  isSelected,
  oddsFormat,
  isSuspended: isSuspendedProp,
  fullWidth = false,
  status: statusProp,
  isHome = false,
  isAway = false,
  edges,
  hasDraw = false,
  isLoading = false,
  hideCompetitor = false,
  variant,
  renderFieldsKey,
  noTopBorder = false,
}: OutcomeProps) => {
  const outcomeName = getOutcomeName(outcome, competitor, renderFieldsKey);
  const market = outcome.market;
  const marketId = market ? market.id : marketIdProp;
  const status = market ? market.status : statusProp;
  const isLive = market?.bettingType === "Live" || eventStatus === "LIVE";

  // Visible but not bettable. Needs the event too - a market keeps reading
  // Prematch well past kick-off, so the type alone would let a tile through.
  const isLiveViewOnly = useIsLiveViewOnly(eventStatus, market?.bettingType);

  const isResulted = status === "DEACTIVATED" || status === "UNAVAILABLE";
  // Resulted markets keep their final price; UNAVAILABLE is a market that was
  // withdrawn, so there's no price to keep - same reading as the betslip.
  const isSettled = status === "SETTLED" || status === "DEACTIVATED";

  const isSuspended =
    isLiveViewOnly ||
    (market
      ? isSettled ||
        status === "UNAVAILABLE" ||
        status === "SUSPENDED" ||
        outcome.odds === 0 ||
        outcome.odds === 1 ||
        !outcome.active
      : isSuspendedProp ?? false);

  // No market means no status to read - fall back to what the caller told us.
  const displayedOdds =
    !market && isSuspendedProp
      ? 0
      : getDisplayOdds({
          odds: outcome.odds,
          closingOdds: outcome.closingOdds,
          active: outcome.active || undefined,
          marketStatus: status,
          isLiveViewOnly,
        });

  return (
    <UpOrDown
      value={displayedOdds}
      animationLength={10000}
      className={cx(
        outcomeVariants({ variant }),
        {
          [styles.draw]: isDraw,
          [styles.fullWidth]: fullWidth,
          [styles.home]: isHome,
          [styles.away]: isAway,
          [styles.hasDraw]: hasDraw,
          [styles.roundBottomLeft]: edges?.roundBottomLeft,
          [styles.roundBottomRight]: edges?.roundBottomRight,
          [styles.dividerLeft]: edges?.dividerLeft,
          [styles.dividerRight]: edges?.dividerRight,
          [styles.bottomBorder]: edges?.bottomBorder,
        },
        classNameProp,
      )}
    >
      <div
        onClick={() => {
          if (isLoading || isLiveViewOnly) return;
          toggleSelection({
            type: isOutright ? "outright" : "match",
            outcomeId: id,
            marketId: marketId,
            sourceId: eventId,
            odds: outcome.odds,
            hasPromotionalMarkets: !!outcome?.market?.hasPromotionalMarkets,
            hasLiveMarkets: isLive,
            eventId,
          });
        }}
        className={cx(styles.outcome, {
          [styles[`${align}Align`]]: true,
          [styles.live]: isLive,
          [styles.selected]: isSelected,
          [styles.outright]: isOutright,
          [styles.disabled]: isResulted,
          [styles.suspended]: isSuspended,
          //let user unselect a disabled outcome
          [styles.unclickable]: isSuspended && !isSelected,
          [styles.won]: outcome.result === "WIN",
          [styles.lost]: outcome.result === "LOSE" || outcome.result === "VOID",
          [styles.loading]: isLoading,
          [styles.noTopBorder]: noTopBorder,
        })}
      >
        <div className={styles.detail}>
          <div className={styles.name}>{outcomeName}</div>
          {typeof outcome.odds !== "undefined" && (
            <div className={styles.oddsContainer}>
              <Odds
                className={cx(styles.odds, {
                  [styles.suspended]: isSuspended && outcome.result !== "WIN",
                })}
                base={displayedOdds}
                format={oddsFormat}
              />
              <div className={styles.arrowUp}>
                <ArrowUp />
              </div>

              <div className={styles.arrowDown}>
                <ArrowDown />
              </div>
            </div>
          )}
        </div>
        {!isDraw && !hideCompetitor && (
          <SportsIcon
            outcomeType={outcome.type}
            competitor={competitor ?? undefined}
            size={isOutright ? 30 : 24}
            className={styles.competitor}
            reserveAvatarSpace
          />
        )}
      </div>
    </UpOrDown>
  );
};

export { Outcome, getOutcomeName };
